import React, { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, FormControl, InputLabel, Link, MenuItem, Select, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material'
import FileUploadService from '../../../../../shared/services/file-upload/file-upload.service'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditOutlined, HighlightOff, RefreshOutlined, SaveOutlined } from '@mui/icons-material'
import gaiaTheme from '../../../../../root-components/theme'
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications'
import { CsvUploadEmissions } from '../../../../presentation-components/settings-components/data-import/csv-upload-emissions'
import { CsvUploadVehicles } from '../../../../presentation-components/settings-components/data-import/csv-upload-vehicles'
import { IFileUpload } from '../../../../../shared/services/file-upload/file-upload.model'
import { CsvUploadSpendBased } from '../../../../presentation-components/settings-components/data-import/csv-upload-spend-based'
import { a11yProps, deleteQueryParamFromUrl } from '../../../../../shared/utils/misc-shared-utils'
import { CustomTabPanel } from '../../../../presentation-components/display-elements-components/custom-tab-panel'
import { LoadingButton } from '@mui/lab'
import { CsvUploadGood } from '../../../../presentation-components/settings-components/data-import/csv-upload-good'
import { CsvUploadDirectEmissions } from '../../../../presentation-components/settings-components/data-import/csv-upload-emissions-direct'
import { LoadingPage } from '../../../loading-page'
import { useReportPeriodWatcher } from '../../../../../hooks/use-report-period-watcher'
import { IEditFileUploadLineLocation, IEditFileUploadLineProject, IEditFileUploadLineSupplier, IFileUploadLine, IFileUploadLineDirectEmission, IFileUploadLineGood, IFileUploadLineGoodEmission, IFileUploadLineLocation, IFileUploadLineProject, IFileUploadLineSpendBased, IFileUploadLineSupplier, IFileUploadLineVehicle, IPostFileUploadLine, IValidationError } from '../../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model'
import FileUploadAllLineService from '../../../../../shared/services/file-upload-all-lines/file-upload-all-lines.service'
import { PageOuterContainer } from '../../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../../presentation-components/display-elements-components/page-heading'
import { IconName } from '../../../../../shared/interfaces/icon-picker.model'
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked'
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels'
import { CsvUploadGoodEmissions } from '../../../../presentation-components/settings-components/data-import/csv-upload-emissions-good'
import { CsvUploadProjects } from '../../../../presentation-components/settings-components/data-import/csv-upload-projects'
import { CsvUploadLocations } from '../../../../presentation-components/settings-components/data-import/csv-upload-locations'
import { CsvUploadSuppliers } from '../../../../presentation-components/settings-components/data-import/csv-upload-supplier'
import { DeleteImportFileDialog } from '../../../../dialogs/settings/data-import/delete-import-file-dialog'
import { EditBulkUploadLinesDialog } from '../../../../dialogs/settings/data-import/edit-bulk-upload-lines'
import { ImportProcessingIndicator } from '../../../../presentation-components/display-elements-components/import-processing-indicator'
import { useBillDocuments } from '../../../../../hooks/use-bill-documents'
import { BillDocumentsList, BillListFilter } from '../../../../presentation-components/settings-components/data-import/bill-documents-list'
import { BillReview } from '../../../../presentation-components/settings-components/data-import/bill-review'
import { billNeedsALook } from '../../../../../shared/utils/bill-upload-utils'
import { isLiveDuplicate } from '../../../../../shared/utils/bill-review-tasks'
import { LoadingSpinner } from '../../../../presentation-components/display-elements-components/loading-spinner'

const IN_PROGRESS_TAB_INDEX = 3
// Bill uploads only: one row per bill, with what became of it. The tabs carry explicit values, so the
// conditional In progress tab never shifts this one (or the ?tabId=1 links to Imported).
const BILLS_TAB_INDEX = 4

export const DataImportUploadDetailPage = () => {
  const { id, uploadId } = useParams();
  const [searchParams] = useSearchParams();
  const reportPeriodWatcher = useReportPeriodWatcher({ redirectUrl: `/settings/data-upload/import-history/${id}` })
  const navigate = useNavigate()
  //Services
  const fileUploadService = new FileUploadService();
  const fileUploadAllLineService = new FileUploadAllLineService();
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext);
  const screenLabelsContext = useContext(ScreenLabelsContext)
  //State
  const [fileUpload, setFileUpload] = useState<IFileUpload>();
  const [editUploadLocationLine, setEditUploadLocationLine] = useState<IFileUploadLineLocation>()
  const [editUploadEmissionLine, setEditUploadEmissionLine] = useState<IFileUploadLine>()
  const [editUploadVehicleLine, setEditUploadVehicleLine] = useState<IFileUploadLineVehicle>()
  const [editUploadSpendBasedLine, setEditUploadSpendBasedLine] = useState<IFileUploadLineSpendBased>()
  const [editUploadGoodLine, setEditUploadGoodLine] = useState<IFileUploadLineGood>()
  const [editUploadGoodEmissionLine, setEditUploadGoodEmissionLine] = useState<IFileUploadLineGoodEmission>()
  const [editUploadDirectEmissionLine, setEditUploadDirectEmissionLine] = useState<IFileUploadLineDirectEmission>()
  const [editUploadSupplierLine, setEditUploadSupplierLine] = useState<IFileUploadLineSupplier>()
  const [editUploadProjectLine, setEditUploadProjectLine] = useState<IFileUploadLineProject>();
  const [openEmissionDialog, setDialogEmissionOpen] = React.useState(false);
  const [openGoodEmissionDialog, setDialogGoodEmissionOpen] = React.useState(false);
  const [openDirectEmissionDialog, setDialogDirectEmissionOpen] = React.useState(false);
  const [openVehicleDialog, setDialogVehicleOpen] = React.useState(false);
  const [openSpendBasedDialog, setDialogSpendBasedOpen] = React.useState(false);
  const [openGoodDialog, setDialogGoodOpen] = React.useState(false);
  const [openProjectDialog, setDialogProjectOpen] = React.useState(false);
  const [openSupplierDialog, setDialogSupplierOpen] = React.useState(false);
  const [openLocationDialog, setDialogLocationOpen] = React.useState(false);
  const [openDeleteFileDialog, setOpenDeleteFileDialog] = React.useState(false);
  const [value, setValue] = React.useState(0);
  // Decoupled-commit flow: async worker commit progress for the "In progress" tab.
  const [hasInProgress, setHasInProgress] = useState<boolean>(false);
  const [commitProgress, setCommitProgress] = useState<any>(null);
  const [validatedUploadLines, setValidatedUploadLines] = useState<IPostFileUploadLine[]>();
  const [pendingUploadLines, setPendingUploadLines] = useState<IPostFileUploadLine[]>();
  const [fileUploadGeneralLines, setFileUploadGeneralLines] = useState<IFileUploadLine[] | IFileUploadLineVehicle[] | IFileUploadLineGoodEmission[] | IFileUploadLineDirectEmission[] | IFileUploadLineSpendBased[] | IFileUploadLineGood[] | IFileUploadLineProject[] | IFileUploadLineSupplier[] | IFileUploadLineLocation[]>();
  const [fileUploadGeneralLinesMatched, setFileUploadGeneralLinesMatched] = useState<IFileUploadLine[] | IFileUploadLineVehicle[] | IFileUploadLineGoodEmission[] | IFileUploadLineDirectEmission[] | IFileUploadLineSpendBased[] | IFileUploadLineGood[] | IFileUploadLineProject[] | IFileUploadLineSupplier[] | IFileUploadLineLocation[]>();
  const [validationErrors, setValidationErrors] = useState<IValidationError[]>();
  const [errorCode, setErrorCode] = useState<number>();
  const [errorMessage, setErrorMessage] = useState<string>();
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedPending, setSelectedPending] = useState<string[]>([]);
  const [selectAllFilteredPending, setSelectAllFilteredPending] = useState<boolean>(false);
  const [selectedMatched, setSelectedMatched] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [inputPageMatched, setInputPageMatched] = useState(0);
  const [totalRecordsMatched, setTotalRecordsMatched] = useState(0)
  const [perPageMatched, setPerPageMatched] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  const [orderByMatched, setOrderByMatched] = useState<string>()
  const [orderMatched, setOrderMatched] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isFileLoading, setIsFileLoading] = useState<boolean>(true)
  const [isFileLinesLoading, setIsFileLinesLoading] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  const [isCommitSubmitting, setIsCommitSubmitting] = useState<boolean>(false)
  const [isCommitAllSubmitting, setIsCommitAllSubmitting] = useState<boolean>(false)
  const [isDiscardSubmitting, setIsDiscardSubmitting] = useState<boolean>(false)
  const [isReRunningValidation, setIsReRunningValidation] = useState<boolean>(false)
  const [openTransactionDialog, setTransactionDialogOpen] = useState<boolean>(false)
  const [selectedFilterField, setSelectedFilterField] = useState<string>('')
  const [filteredLines, setFilteredLines] = useState<IFileUploadLine[] | IFileUploadLineVehicle[] | IFileUploadLineGoodEmission[] | IFileUploadLineDirectEmission[] | IFileUploadLineSpendBased[] | IFileUploadLineGood[] | IFileUploadLineProject[] | IFileUploadLineSupplier[] | IFileUploadLineLocation[]>()
  const [billListFilter, setBillListFilter] = useState<BillListFilter | null>(null)
  const [isImportingBillRows, setIsImportingBillRows] = useState<boolean>(false)
  //Variables
  const filteredTotalRecords = selectedFilterField && filteredLines ? filteredLines.length : totalRecords
  const normaliseValidationErrors = (errors: any): any[] => {
    if (Array.isArray(errors)) return errors
    if (typeof errors === 'string') {
      try {
        const parsed = JSON.parse(errors)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }
    if (errors && typeof errors === 'object') {
      return Object.values(errors).flat().filter(Boolean)
    }
    return []
  }
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const isStandardEmissions = fileUpload?.details === 'emissions-rules' || fileUpload?.details === 'emissions-manual'
  const isLinkedActivityImport = isStandardEmissions && fileUpload?.linkToExistingEmission === true
  // Emission/spend imports use the decoupled edit → explicit worker-commit flow (unified list,
  // Ready/Failed chips, Commit Selected, In progress tab). Non-linked only.
  const isDecoupledCommitType = (id === 'emissions' || id === 'spend-based') && !isLinkedActivityImport
  // Bills read from PDFs or images. Their rows are shown with the bill they came from, a Bills tab
  // says what became of every bill, and nothing imports until the reviewer presses Import: the
  // review step promised "nothing is imported until you confirm", and an edit must not quietly
  // import every other row (possible duplicates included) the way a spreadsheet's decoupled flow does.
  const isBillUpload = fileUpload?.sourceType === 'BILL_DOCUMENTS' && isStandardEmissions
  const { documents: billDocuments, progress: billProgress, isLoading: isBillDocumentsLoading, isReading: isBillDocumentsReading, refresh: refreshBills } = useBillDocuments(
    isBillUpload ? fileUpload?.id : null,
    // The rows only exist once every bill has been read.
    () => setChange(c => c + 1),
  )
  const effectiveBillListFilter: BillListFilter = billListFilter ?? (billDocuments.some(billNeedsALook) ? 'attention' : 'all')

  //Use Effects
  useEffect(() => {
    if (searchParams.has('tabId')) {
      const tab = searchParams.get('tabId')
      if (tab) {
        setValue(Number(tab))
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
    getUploads();
  }, [])


  const getUploads = () => {
    if (uploadId) {
      fileUploadService.getUpload(uploadId)
        .then(response => {
          const commitActive = response.lineCommitStatus === 'IN_PROGRESS' || response.lineCommitStatus === 'PENDING'
          const scopedCommit = (id === 'emissions' || id === 'spend-based') && response.linkToExistingEmission !== true
          if (commitActive && !scopedCommit) {
            // Non-emission / linked imports keep the full-page commit-progress redirect.
            navigate(`/settings/data-upload/commit-progress/${response.id}?returnTo=detail&id=${id}&uploadId=${response.id}`)
          }
          setFileUpload(response)
          setIsFileLoading(false)
          // Emission/spend imports surface commit progress inline via the "In progress" tab.
          if (scopedCommit) {
            pollCommitProgress(response.id)
          }
        })
        .catch(err => console.log(err))
    }
  }

  // Tracks whether the previous poll saw work in flight, so we can fire the completion snackbar
  // exactly once on the in-flight → done transition.
  const wasInProgressRef = useRef<boolean>(false)

  // Poll the async commit progress for the decoupled flow. Drives the "In progress" tab; when
  // the run finishes (no lines still in flight) it refreshes the list, leaves the tab, and
  // reports the outcome.
  const pollCommitProgress = (fileUploadId: string) => {
    fileUploadService.checkFileUploadCommitProgress(fileUploadId)
      .then((resp: any) => {
        setCommitProgress(resp)
        const inProgress = (resp?.inProgressCount ?? 0) > 0
        if (wasInProgressRef.current && !inProgress) {
          // The commit run just finished.
          const committed = Math.max(0, Number(resp?.numberOfRecords || 0) - Number(resp?.alreadyCommittedCount || 0))
          const failed = Number(resp?.failedCount || 0)
          setChange(c => c + 1)
          setValue(v => (v === IN_PROGRESS_TAB_INDEX ? 0 : v))
          if (committed > 0 && failed > 0) {
            userNotificationsContext.openSnackbar({ open: true, message: `${committed} record${committed === 1 ? '' : 's'} imported successfully, ${failed} failed`, backgroundColor: 'text.primary' })
          } else if (committed > 0) {
            userNotificationsContext.openSnackbar({ open: true, message: `${committed} record${committed === 1 ? '' : 's'} imported successfully` })
          } else if (failed > 0) {
            userNotificationsContext.openSnackbar({ open: true, message: `${failed} record${failed === 1 ? '' : 's'} failed to import`, backgroundColor: 'text.primary' })
          }
        }
        wasInProgressRef.current = inProgress
        setHasInProgress(inProgress)
      })
      .catch(err => console.log(err))
  }

  // While a commit is in flight, poll every 5s.
  useEffect(() => {
    if (!hasInProgress || !fileUpload) return
    const interval = setInterval(() => pollCommitProgress(fileUpload.id), 5000)
    return () => clearInterval(interval)
  }, [hasInProgress, fileUpload])

  useEffect(() => {
    getUploadLines()
  }, [fileUpload, inputPage, perPage, inputPageMatched, perPageMatched, change])

  // Rows were edited, discarded, imported or re-validated: the bills' row counts have moved too.
  // Skips the first run, which the bill hook's own first fetch already covers.
  const billRefreshArmedRef = useRef<boolean>(false)
  useEffect(() => {
    if (!isBillUpload) return
    if (!billRefreshArmedRef.current) {
      billRefreshArmedRef.current = true
      return
    }
    refreshBills()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change, fileUpload])

  // Exit "select all pending" whenever the underlying pending set can change (tab, page
  // size, or the client-side field filter). NOT on page navigation, so it persists across
  // pages like the emissions-overview table.
  useEffect(() => {
    setSelectAllFilteredPending(false)
  }, [value, perPage, selectedFilterField])

  const getUploadLines = () => {
    if (fileUpload) {
      if (value === IN_PROGRESS_TAB_INDEX || value === BILLS_TAB_INDEX || (value === 0 && isBillUpload)) {
        // The "In progress" tab is driven by commit polling, the Bills tab by the bill list, and a
        // bill upload's Pending tab by the bill review, which fetches its own rows.
        setIsFileLinesLoading(false)
        return
      }
      setIsFileLinesLoading(true)
      setIsLoading(true)
      if (value === 0 && isDecoupledCommitType) {
        // Unified list: Error + Ready + Failed rows in one table, in row order. Rows currently
        // being committed are excluded server-side (they show on the "In progress" tab).
        fileUploadAllLineService.getUploadLinesWithPaginationByFile(fileUpload.details, fileUpload.id, 'uncommitted', inputPage, perPage, orderBy, order)
          .then(response => {
            setIsFileLinesLoading(false)
            setFileUploadGeneralLines(response.rows)
            setTotalRecords(response.count)
            setFileUploadGeneralLinesMatched([])
            setTotalRecordsMatched(0)
          })
          .catch(err => {
            console.log(err)
            setIsFileLinesLoading(false)
          })
      } else if (value === 0) {
        let completedRequests = 0
        const checkLoadingStatus = () => {
          completedRequests += 1;
          if (completedRequests === 2) {
            setIsFileLinesLoading(false);
          }
        };
        fileUploadAllLineService.getUploadLinesWithPaginationByFile(fileUpload.details, fileUpload.id, 'pending', inputPage, perPage, orderBy, order)
          .then(response => {
            checkLoadingStatus()
            setFileUploadGeneralLines(response.rows)
            setTotalRecords(response.count)
          })
          .catch(err => {
            console.log(err)
            setIsFileLinesLoading(false)
          })
        fileUploadAllLineService.getUploadLinesWithPaginationByFile(fileUpload.details, fileUpload.id, 'matched', inputPageMatched, perPageMatched, orderByMatched, orderMatched)
          .then(response => {
            checkLoadingStatus()
            setFileUploadGeneralLinesMatched(response.rows)
            setTotalRecordsMatched(response.count)
          })
          .catch(err => {
            console.log(err)
            setIsFileLinesLoading(false)
          })
      } else if (value === 1) {
        fileUploadAllLineService.getUploadLinesWithPaginationByFile(fileUpload.details, fileUpload.id, 'committed', inputPage, perPage, orderBy, order)
          .then(response => {
            setIsFileLinesLoading(false)
            setFileUploadGeneralLines(response.rows)
            setTotalRecords(response.count)
          })
          .catch(err => {
            console.log(err)
            setIsFileLinesLoading(false)
          })
      } else if (value === 2) {
        fileUploadAllLineService.getUploadLinesWithPaginationByFile(fileUpload.details, fileUpload.id, 'discarded', inputPage, perPage, orderBy, order)
          .then(response => {
            setIsFileLinesLoading(false)
            setFileUploadGeneralLines(response.rows)
            setTotalRecords(response.count)
          })
          .catch(err => {
            console.log(err)
            setIsFileLinesLoading(false)
          })
      }
    }
  }

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
      if (value !== 0) {
        setInputPageMatched(0)
      }
    }
    setHasPageMounted(true)
  }, [value, order, orderBy, orderMatched, orderByMatched])

  useEffect(() => {
    if (!isFileLinesLoading && !isFileLoading) {
      setIsLoading(false)
    }
  }, [isFileLinesLoading, isFileLoading])

  const getUniqueValidationErrorFields = (): string[] => {
    if (!fileUploadGeneralLines || value !== 0) {
      return []
    }
    const fieldSet = new Set<string>()
    fileUploadGeneralLines.forEach((line: any) => {
      const validationErrors = normaliseValidationErrors(line.validationErrors)
      if (validationErrors.length > 0) {
        validationErrors.forEach((error: any) => {
          if (error.field && error.field.trim() !== '') {
            fieldSet.add(error.field)
          }
        })
      } else if (line.linkStatus === 'REVIEW') {
        fieldSet.add('Emission match')
      }
    })
    return Array.from(fieldSet).sort()
  }

  useEffect(() => {
    if (!fileUploadGeneralLines || value !== 0) {
      setFilteredLines(fileUploadGeneralLines)
      return
    }
    if (!selectedFilterField) {
      setFilteredLines(fileUploadGeneralLines)
      return
    }
    const filtered = (fileUploadGeneralLines as any[]).filter((line: any) =>
      normaliseValidationErrors(line.validationErrors).some((error: any) => error.field === selectedFilterField)
      || (line.linkStatus === 'REVIEW' && selectedFilterField === 'Emission match')
    )
    setFilteredLines(filtered)
  }, [fileUploadGeneralLines, selectedFilterField, value])

  const handleFilterChange = (field: string) => {
    setSelectedFilterField(field)
    setInputPage(0)
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setFileUploadGeneralLines([])
    setIsLoading(true)
    setIsFileLinesLoading(true)
    setSelectedFilterField('')
  };

  // Delete the Entire File
  const deleteFileHandler = (deleteAssociatedRecords: boolean) => {
    if (uploadId && !isDialogSubmitting) {
      setIsDialogSubmitting(true)
      fileUploadService.deleteUpload(uploadId, deleteAssociatedRecords)
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
          navigate(`/settings/data-upload/import-history`)
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          console.log(err)
        })
        .finally(() => {
          setIsDialogSubmitting(false)
          setOpenDeleteFileDialog(false)
        })
    }
  }

  const closeDialogHandler = () => {
    setDialogEmissionOpen(false);
    setDialogGoodEmissionOpen(false)
    setDialogDirectEmissionOpen(false)
    setDialogVehicleOpen(false);
    setDialogSpendBasedOpen(false)
    setDialogGoodOpen(false)
    setDialogProjectOpen(false)
    setDialogSupplierOpen(false)
    setDialogLocationOpen(false)
    setEditUploadSpendBasedLine(undefined)
    setEditUploadVehicleLine(undefined)
    setEditUploadEmissionLine(undefined)
    setEditUploadGoodEmissionLine(undefined)
    setEditUploadDirectEmissionLine(undefined)
    setEditUploadGoodLine(undefined)
    setEditUploadProjectLine(undefined)
    setEditUploadSupplierLine(undefined)
    setEditUploadLocationLine(undefined)
    setValidationErrors(undefined);
    setErrorCode(undefined);
    setErrorMessage(undefined);
  };

  const editFileUploadLineClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, uploadLine: any) => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      e.stopPropagation()
      if (id === 'emissions' && (fileUpload?.details === 'emissions-rules' || fileUpload?.details === 'emissions-manual')) {
        setDialogEmissionOpen(true);
        setEditUploadEmissionLine(uploadLine)
      } else if (id === 'emissions' && fileUpload?.details === 'emissions-product') {
        setDialogGoodEmissionOpen(true);
        setEditUploadGoodEmissionLine(uploadLine)
      } else if (id === 'emissions' && fileUpload?.details === 'emissions-direct') {
        setDialogDirectEmissionOpen(true);
        setEditUploadDirectEmissionLine(uploadLine)
      } else if (id === 'vehicles') {
        setDialogVehicleOpen(true);
        setEditUploadVehicleLine(uploadLine)
      } else if (id === 'spend-based') {
        setDialogSpendBasedOpen(true);
        setEditUploadSpendBasedLine(uploadLine)
      } else if (id === 'products') {
        setDialogGoodOpen(true);
        setEditUploadGoodLine(uploadLine)
      } else if (id === 'projects') {
        setDialogProjectOpen(true);
        setEditUploadProjectLine(uploadLine as IFileUploadLineProject)
      } else if (id === 'suppliers') {
        setDialogSupplierOpen(true);
        setEditUploadSupplierLine(uploadLine as IFileUploadLineSupplier)
      } else if (id === 'locations') {
        setDialogLocationOpen(true);
        setEditUploadLocationLine(uploadLine as IFileUploadLineLocation)
      }
    }
  }

  const deleteOneUploadLine = (uploadLineId: string) => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to discard this record? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response && fileUpload?.details) {
            fileUploadAllLineService.deleteUploadLine(uploadLineId, fileUpload.details)
              .then(response => {
                deleteFinishHandler()
              })
              .catch((err) => {
                if (err.response.data.code === 1) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                } else {
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                }
                console.log(err)
              })
          }
        })
    }
  }

  const deleteMultipleUploadLine = (fromPending: boolean = false) => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to discard this record(s)? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          const linesToDelete = fromPending ? pendingUploadLines : validatedUploadLines
          if (response && fileUpload && fileUpload.details && linesToDelete) {
            if (!isDiscardSubmitting) {
              setIsDiscardSubmitting(true)
              fileUploadAllLineService.deleteUploadLines(linesToDelete, fileUpload.details)
                .then(response => {
                  deleteFinishHandler()
                  setIsDiscardSubmitting(false)
                })
                .catch((err) => {
                  if (err.response.data.code === 1) {
                    userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                  } else {
                    userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  }
                  setIsDiscardSubmitting(false)
                  console.log(err)
                })
            }
          }
        })
    }
  }

  const deleteFinishHandler = () => {
    userNotificationsContext.openSnackbar({ open: true, message: 'Discarded successfully' });
    getUploads();
    setSelected([])
    setSelectedPending([])
    setSelectAllFilteredPending(false)
    setSelectedMatched([])
    setPendingUploadLines([])
    setValidatedUploadLines([])
    setSelectedFilterField('')
  }

  const submitEditUploadLine = (lineId: string, editUploadLine: any) => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      if (fileUpload?.details) {
        if (!isDialogSubmitting) {
          setIsDialogSubmitting(true)
          setValidationErrors(undefined)
          setErrorCode(undefined)
          setErrorMessage(undefined)
          fileUploadAllLineService.editUploadLine(editUploadLine, lineId, fileUpload?.details, false, isDecoupledCommitType ? 'create=false' : 'create=true')
            .then(response => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
              setIsDialogSubmitting(false)
              closeDialogHandler();
              // Newly-valid lines are handed straight to the worker to commit asynchronously. Bill
              // uploads wait for the Import button instead.
              if (isDecoupledCommitType && !isBillUpload) {
                enqueueReadyCommit()
              } else if (isBillUpload) {
                setChange(c => c + 1)
              } else {
                getUploads();
              }
            })
            .catch(err => {
              console.log(err)
              if (err.response?.data?.code && err.response?.data?.message) {
                if (err.response.data.code === 100) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                } else {
                  setErrorCode(err.response.data.code)
                  setErrorMessage(err.response.data.message)
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                }
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              setIsDialogSubmitting(false)
            })
        }
      }
    }
  }

  const saveSelectedUploadLines = () => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      if (validatedUploadLines && fileUpload && fileUpload.details) {
        if (!isCommitSubmitting) {
          setIsCommitSubmitting(true)
          fileUploadAllLineService.createUploadLine(validatedUploadLines, fileUpload?.details)
            .then(response => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
              getUploads();
              setSelectedMatched([])
              setIsCommitSubmitting(false)
            })
            .catch(err => {
              console.log(err)
              if (!err.response || err?.response?.status === 504) {
                navigate('/time-out?type=import')
              } else if (err.response?.data?.code && err.response?.data?.message) {
                if (err.response.data.code === 100) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                } else {
                  userNotificationsContext.openDialog({ active: true, title: 'Action Required', message: err.response?.data?.message, buttonCopy: 'Ok', hideCancelButton: true });
                }
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              setIsCommitSubmitting(false)
            })
        }
      }
    }
  }

  const saveAllSelectedUploadLines = () => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      if (fileUpload && fileUpload.details) {
        if (!isCommitAllSubmitting) {
          setIsCommitAllSubmitting(true)
          fileUploadAllLineService.createUploadLine([], fileUpload.details, `commitAll=true&fileUploadId=${fileUpload.id}`)
            .then(response => {
              // Navigate to commit progress page
              navigate(`/settings/data-upload/commit-progress/${fileUpload.id}?returnTo=detail&id=${id}&uploadId=${fileUpload.id}`)
            })
            .catch(err => {
              if (err?.response?.status === 504) {
                navigate('/time-out?type=import')
              } else if (err.response?.data?.code && err.response?.data?.message) {
                if (err.response.data.code === 100) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                } else {
                  userNotificationsContext.openDialog({ active: true, title: 'Action Required', message: err.response?.data?.message, buttonCopy: 'Ok', hideCancelButton: true });
                }
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              setIsCommitAllSubmitting(false)
            })
        }
      }
    }
  }

  const reRunValidationHandler = () => {
    if (isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) {
      userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
    } else {
      setIsReRunningValidation(true)
      if (uploadId) {
        fileUploadService.reRunValidation(uploadId)
          .then(response => {
            getUploads();
            setSelectedFilterField('')
            setIsReRunningValidation(false)
            // The bill review fetches its own rows.
            if (isBillUpload) setChange(c => c + 1)
          })
          .catch(err => {
            console.log(err)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            setIsReRunningValidation(false)
          })
      }
    }
  }

  const handleOpenEditDialog = () => {
    setTransactionDialogOpen(true)
  }

  const closeTransactionDialogHandler = () => {
    setTransactionDialogOpen(false)
  }

  const successTransactionDialogHandler = () => {
    closeTransactionDialogHandler()
    setSelectedPending([])
    setSelectAllFilteredPending(false)
    setPendingUploadLines([])
    setSelectedFilterField('')
    // After a bulk edit, hand every now-ready line to the worker to commit asynchronously. Bill
    // uploads wait for the Import button instead.
    if (isDecoupledCommitType && !isBillUpload) {
      enqueueReadyCommit()
    } else if (isBillUpload) {
      setChange(c => c + 1)
    } else {
      getUploads()
    }
  }

  const handleExitSelectAllFilteredPending = (remainingIdsOnPage: string[]) => {
    setSelectAllFilteredPending(false)
    setSelectedPending(remainingIdsOnPage)
  }

  // As soon as an edit leaves lines Ready, hand every ready line to the worker to commit
  // asynchronously (no manual "commit" step). Queued lines surface on the "In progress" tab;
  // any that fail return to the list as Failed. Fire-and-forget — the list refresh + polling
  // reflect the result.
  const enqueueReadyCommit = (): Promise<any> => {
    if (!fileUpload || !isDecoupledCommitType || isReportPeriodLocked) {
      setChange(c => c + 1)
      return Promise.resolve(null)
    }
    return fileUploadAllLineService.commitSelectedFileUploadLines(fileUpload.id, fileUpload.details, { commitAll: true, confirm: true })
      .then((resp) => {
        if (resp?.queued && resp.queued > 0) {
          setSelectedPending([])
          setSelectAllFilteredPending(false)
          setHasInProgress(true)
          setValue(IN_PROGRESS_TAB_INDEX)
          pollCommitProgress(fileUpload.id)
        }
        setChange(c => c + 1)
        return resp
      })
      .catch((err) => {
        // Fire-and-forget for the edit paths, so never rethrown; the import button reads `error`.
        console.log(err)
        setChange(c => c + 1)
        return { error: err }
      })
  }

  // Bill uploads: the reviewer imports the ready rows when they are done checking them. The confirm
  // names any bill that looks like a duplicate, and says the rows that need attention stay behind.
  const importReadyBillRowsHandler = async (readyCount: number) => {
    if (isImportingBillRows || readyCount === 0) return
    const documentsById = Object.fromEntries(billDocuments.map((document) => [document.id, document]))
    const duplicateBills = billDocuments.filter((document) =>
      isLiveDuplicate(document, documentsById)
      && (document.outstandingRowCount || 0) > (document.attentionRowCount || 0))
    const attentionRows = billDocuments.reduce((sum, document) => sum + (document.attentionRowCount || 0), 0)
    const rowsText = `${readyCount.toLocaleString()} ready ${readyCount === 1 ? 'row' : 'rows'}`
    const duplicateText = duplicateBills.length === 0 ? '' : duplicateBills.length === 1
      ? ` ${duplicateBills[0].filename} looks like a duplicate of another bill (same meter and billing period): discard its row first if that bill is already recorded.`
      : ` ${duplicateBills.length} bills look like duplicates of other bills (same meter and billing period): ${duplicateBills.map((document) => document.filename).join(', ')}. Discard their rows first if those bills are already recorded.`
    const attentionText = attentionRows > 0
      ? ` ${attentionRows.toLocaleString()} ${attentionRows === 1 ? 'row is' : 'rows are'} still on your list of things to do and will stay here.`
      : ''
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Import ready rows',
      message: `Import ${rowsText}?${duplicateText}${attentionText}`,
      buttonCopy: 'Import',
      cancelButtonCopy: 'Cancel',
    })
    if (!confirmed) return
    setIsImportingBillRows(true)
    const resp = await enqueueReadyCommit()
    setIsImportingBillRows(false)
    if (resp?.error) {
      userNotificationsContext.openSnackbar({ open: true, message: resp.error?.response?.data?.message || 'There was an error, please try again', backgroundColor: 'text.primary' })
    } else if (!resp?.queued) {
      userNotificationsContext.openSnackbar({ open: true, message: 'Nothing is ready to import', backgroundColor: 'text.primary' })
    }
  }

  const allPendingRowsOnPageSelected = !!fileUploadGeneralLines?.length &&
    (fileUploadGeneralLines as { id: string }[]).every((line) => selectedPending.includes(line.id))
  const showSelectAllPendingBanner = value === 0 && (id === 'emissions' || id === 'spend-based') &&
    !selectedFilterField && !selectAllFilteredPending &&
    allPendingRowsOnPageSelected && totalRecords > (fileUploadGeneralLines?.length ?? 0)


  return (
    <PageOuterContainer>
      {fileUpload &&
        <>
          {id && <PageHeading
            title={fileUpload.filename}
            includeBackButton={true}
            backButtonLink={`/settings/data-upload/import-history`}
            backButtonText='Back to upload data'
            hasTabs={true}
            includeActionButton={true}
            actionButtonIcon={IconName.DeleteOutline}
            actionButtonHandler={() => setOpenDeleteFileDialog(true)}
            actionButtonText='Delete'
            hideShowActionButton={!(id === 'emissions' || id === 'spend-based')}
          />}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
              <Tab label={isLinkedActivityImport ? 'Needs Attention' : 'Pending'} value={0} {...a11yProps(0)} />
              <Tab label="Imported" value={1} {...a11yProps(1)} />
              <Tab label="Discarded" value={2} {...a11yProps(2)} />
              {isBillUpload && <Tab label={billDocuments.length > 0 ? `Bills (${billDocuments.length})` : 'Bills'} value={BILLS_TAB_INDEX} {...a11yProps(BILLS_TAB_INDEX)} />}
              {hasInProgress && <Tab label="In progress" value={IN_PROGRESS_TAB_INDEX} {...a11yProps(IN_PROGRESS_TAB_INDEX)} />}
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <LoadingPage pageReady={!isLoading}>
              {isBillUpload ? (
                // Bills get a to-do list instead of the rows table: what to do before the rows can
                // import, then this page's own Import button.
                <BillReview
                  fileUploadId={fileUpload.id}
                  fileType={fileUpload.details}
                  reportPeriodId={fileUpload.reportPeriodId}
                  documents={billDocuments}
                  progress={billProgress}
                  isReading={isBillDocumentsReading}
                  refreshKey={change}
                  onChanged={() => setChange(c => c + 1)}
                  context='history'
                  readOnly={!!isReportPeriodLocked}
                  renderFooter={(readyCount) => (
                    <Stack direction={isMobileMode ? 'column' : 'row'} gap={2} justifyContent='flex-end'>
                      <LoadingButton
                        onClick={reRunValidationHandler}
                        disabled={isImportingBillRows || isReRunningValidation || hasInProgress}
                        startIcon={<RefreshOutlined />}
                        variant='outlined'
                        loading={isReRunningValidation}
                        loadingPosition="start">
                        <Typography>{isReRunningValidation ? 'Checking again' : 'Check every row again'}</Typography>
                      </LoadingButton>
                      <LoadingButton
                        onClick={() => importReadyBillRowsHandler(readyCount)}
                        disabled={readyCount === 0 || isImportingBillRows || isReRunningValidation || hasInProgress}
                        startIcon={<SaveOutlined />}
                        variant='contained'
                        loading={isImportingBillRows}
                        loadingPosition="start">
                        <Typography>{readyCount === 0 ? 'Nothing ready to import' : `Import ${readyCount.toLocaleString()} ready ${readyCount === 1 ? 'row' : 'rows'}`}</Typography>
                      </LoadingButton>
                    </Stack>
                  )}
                />
              ) : (
              <>
              <Typography variant='h5' sx={{ color: 'primary.main', mb: 1 }}>
                {isLinkedActivityImport ? 'Rows Needing Attention' : 'Pending Upload Lines'}
              </Typography>
              <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter</InputLabel>
                  <Select
                    label="Filter"
                    value={selectedFilterField}
                    onChange={(e) => handleFilterChange(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="">
                      <em>All Fields</em>
                    </MenuItem>
                    {getUniqueValidationErrorFields().map((field) => (
                      <MenuItem key={field} value={field}>
                        {field}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!(isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) &&
                  <Stack sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row' }}>
                    <LoadingButton
                      onClick={reRunValidationHandler}
                      disabled={fileUploadGeneralLines?.length === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting || isReRunningValidation || openTransactionDialog}
                      startIcon={<RefreshOutlined />}
                      variant='contained'
                      loading={isReRunningValidation}
                      loadingPosition="start"
                      sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}>
                      <Typography>{isReRunningValidation ? 'Re-Running Validation' : 'Re-Run Validation'}</Typography>
                    </LoadingButton>
                    {(id === 'emissions' || id === 'spend-based') && <Box sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}>
                      <Button
                        variant="contained"
                        onClick={handleOpenEditDialog}
                        disabled={isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting || isReRunningValidation || openTransactionDialog || (selectedPending.length === 0 && !selectAllFilteredPending)}
                        sx={{ minWidth: 100 }}
                        startIcon={<EditOutlined />}
                      >
                        <Typography>
                          Edit Selected ({selectAllFilteredPending ? totalRecords : selectedPending.length})
                        </Typography>
                      </Button>
                    </Box>}
                    <LoadingButton
                      onClick={() => deleteMultipleUploadLine(true)}
                      disabled={totalRecords === 0 || pendingUploadLines?.length === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting || isReRunningValidation || openTransactionDialog}
                      startIcon={<HighlightOff />}
                      variant='outlined'
                      loading={isDiscardSubmitting}
                      loadingPosition="start"
                      sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}>
                      <Typography>{isDiscardSubmitting ? 'Discarding Selected' : 'Discard Selected'}</Typography>
                    </LoadingButton>
                  </Stack>}
              </Stack>
              </>
              )}
              {showSelectAllPendingBanner &&
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    The {fileUploadGeneralLines?.length} rows on this page are selected.{' '}
                    <Link
                      component="button"
                      type="button"
                      underline="always"
                      sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
                      onClick={() => {
                        setSelectAllFilteredPending(true)
                        setSelectedPending([])
                      }}
                    >
                      Select all {totalRecords.toLocaleString()} records
                    </Link>
                    ?
                  </Typography>
                </Alert>}
              {id === 'emissions' && isStandardEmissions && !isBillUpload && fileUploadGeneralLines &&
                <CsvUploadEmissions
                  fileUploadLines={filteredLines as IFileUploadLine[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openEmissionDialog}
                  setOrder={setOrder}
                  uploadId={fileUpload.id}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setSelected={setSelectedPending}
                  selectAllFiltered={selectAllFilteredPending}
                  onExitSelectAllFiltered={handleExitSelectAllFilteredPending}
                  editUploadLine={editUploadEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-product' && fileUploadGeneralLines &&
                <CsvUploadGoodEmissions
                  fileUploadLineGoodEmissions={filteredLines as IFileUploadLineGoodEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodEmissionDialog}
                  isSubmitting={isDialogSubmitting}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  uploadId={fileUpload.id}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  setSelected={setSelectedPending}
                  selectAllFiltered={selectAllFilteredPending}
                  onExitSelectAllFiltered={handleExitSelectAllFilteredPending}
                  editUploadLine={editUploadGoodEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-direct' && fileUploadGeneralLines &&
                <CsvUploadDirectEmissions
                  fileUploadLineDirectEmissions={filteredLines as IFileUploadLineDirectEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openDirectEmissionDialog}
                  isSubmitting={isDialogSubmitting}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  uploadId={fileUpload.id}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  setSelected={setSelectedPending}
                  selectAllFiltered={selectAllFilteredPending}
                  onExitSelectAllFiltered={handleExitSelectAllFilteredPending}
                  editUploadLine={editUploadDirectEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                />}
              {id === 'spend-based' && fileUploadGeneralLines &&
                <CsvUploadSpendBased
                  fileUploadLineSpendBaseds={filteredLines as IFileUploadLineSpendBased[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSpendBasedDialog}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setOrder={setOrder}
                  uploadId={fileUpload.id}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  setSelected={setSelectedPending}
                  selectAllFiltered={selectAllFilteredPending}
                  onExitSelectAllFiltered={handleExitSelectAllFilteredPending}
                  editUploadLine={editUploadSpendBasedLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                />}
              {id === 'locations' && fileUploadGeneralLines &&
                <CsvUploadLocations
                  fileUploadLineLocations={filteredLines as unknown as IFileUploadLineLocation[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openLocationDialog}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setSelected={setSelectedPending}
                  editUploadLine={editUploadLocationLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'vehicles' && fileUploadGeneralLines &&
                <CsvUploadVehicles
                  fileUploadLineVehicle={filteredLines as IFileUploadLineVehicle[]}
                  deleteHandler={deleteOneUploadLine}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openVehicleDialog}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  setSelected={setSelectedPending}
                  validationErrors={validationErrors}
                  editUploadLine={editUploadVehicleLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'products' && fileUploadGeneralLines &&
                <CsvUploadGood
                  fileUploadLineGoods={filteredLines as IFileUploadLineGood[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodDialog}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  setSelected={setSelectedPending}
                  editUploadLine={editUploadGoodLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'projects' && fileUploadGeneralLines &&
                <CsvUploadProjects
                  fileUploadLineProjects={filteredLines as IFileUploadLineProject[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openProjectDialog}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setOrder={setOrder}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  setSelected={setSelectedPending}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadProjectLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineProject) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'suppliers' && fileUploadGeneralLines &&
                <CsvUploadSuppliers
                  fileUploadLineSuppliers={filteredLines as IFileUploadLineSupplier[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSupplierDialog}
                  setValidatedUploadLines={setPendingUploadLines}
                  selected={selectedPending}
                  setOrder={setOrder}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  setSelected={setSelectedPending}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadSupplierLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineSupplier) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={filteredTotalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'pending'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {totalRecordsMatched > 0 &&
                <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
                  <Typography variant='h5' sx={{ color: 'primary.main' }}>
                    {isLinkedActivityImport ? 'Ready to Import' : 'Matched Upload Lines'}
                  </Typography>
                  {!(isReportPeriodLocked && (id === 'emissions' || id === 'spend-based')) &&
                    <Stack sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row' }}>
                      <LoadingButton
                        onClick={saveSelectedUploadLines}
                        disabled={validatedUploadLines?.length === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting || isReRunningValidation || openTransactionDialog}
                        startIcon={<SaveOutlined />}
                        variant='contained'
                        loading={isCommitSubmitting}
                        sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}
                        loadingPosition="start">
                        <Typography>{isCommitSubmitting ? 'Importing Selected' : 'Import Selected'}</Typography>
                      </LoadingButton>
                      <LoadingButton
                        onClick={saveAllSelectedUploadLines}
                        disabled={totalRecordsMatched === 0 || isCommitAllSubmitting || isDiscardSubmitting || isCommitSubmitting || isReRunningValidation || openTransactionDialog}
                        startIcon={<SaveOutlined />}
                        variant='contained'
                        loading={isCommitAllSubmitting}
                        sx={{ mr: isMobileMode ? 0 : 2, mb: isMobileMode ? 2 : 0 }}
                        loadingPosition="start">
                        <Typography>{isCommitAllSubmitting ? 'Importing All' : 'Import All'}</Typography>
                      </LoadingButton>
                      <LoadingButton
                        onClick={() => deleteMultipleUploadLine(false)}
                        disabled={validatedUploadLines?.length === 0 || isCommitSubmitting || isDiscardSubmitting || isCommitAllSubmitting || isReRunningValidation || openTransactionDialog}
                        startIcon={<HighlightOff />}
                        variant='outlined'
                        loading={isDiscardSubmitting}
                        loadingPosition="start">
                        <Typography>{isDiscardSubmitting ? 'Discarding Selected' : 'Discard Selected'}</Typography>
                      </LoadingButton>
                    </Stack>}
                </Stack>}
              {id === 'emissions' && isStandardEmissions && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadEmissions
                  fileUploadLines={fileUploadGeneralLinesMatched as IFileUploadLine[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openEmissionDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  uploadId={fileUpload.id}
                  isSubmitting={isDialogSubmitting}
                  setSelected={setSelectedMatched}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadEmissionLine}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setPerPage={setPerPageMatched}
                  status={'validated'}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-product' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadGoodEmissions
                  fileUploadLineGoodEmissions={fileUploadGeneralLinesMatched as IFileUploadLineGoodEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodEmissionDialog}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  uploadId={fileUpload.id}
                  isSubmitting={isDialogSubmitting}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  setPerPage={setPerPageMatched}
                  setSelected={setSelectedMatched}
                  editUploadLine={editUploadGoodEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  status={'validated'}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-direct' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadDirectEmissions
                  fileUploadLineDirectEmissions={fileUploadGeneralLinesMatched as IFileUploadLineDirectEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openDirectEmissionDialog}
                  isSubmitting={isDialogSubmitting}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  uploadId={fileUpload.id}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  setPerPage={setPerPageMatched}
                  setSelected={setSelectedMatched}
                  editUploadLine={editUploadDirectEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  status={'validated'}
                />}
              {id === 'spend-based' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadSpendBased
                  fileUploadLineSpendBaseds={fileUploadGeneralLinesMatched as IFileUploadLineSpendBased[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSpendBasedDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  isSubmitting={isDialogSubmitting}
                  setOrder={setOrderMatched}
                  uploadId={fileUpload.id}
                  setOrderBy={setOrderByMatched}
                  setPerPage={setPerPageMatched}
                  deleteHandler={deleteOneUploadLine}
                  setSelected={setSelectedMatched}
                  editUploadLine={editUploadSpendBasedLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  status={'validated'}
                />}
              {id === 'locations' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadLocations
                  fileUploadLineLocations={fileUploadGeneralLinesMatched as unknown as IFileUploadLineLocation[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openLocationDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  setSelected={setSelectedMatched}
                  editUploadLine={editUploadLocationLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setPerPage={setPerPageMatched}
                  status={'validated'}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  isSubmitting={isDialogSubmitting}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'products' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadGood
                  fileUploadLineGoods={fileUploadGeneralLinesMatched as IFileUploadLineGood[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  isSubmitting={isDialogSubmitting}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  setPerPage={setPerPageMatched}
                  setSelected={setSelectedMatched}
                  editUploadLine={editUploadGoodLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  status={'validated'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'projects' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadProjects
                  fileUploadLineProjects={fileUploadGeneralLinesMatched as IFileUploadLineProject[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openProjectDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  isSubmitting={isDialogSubmitting}
                  setSelected={setSelectedMatched}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadProjectLine}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineProject) => void}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setPerPage={setPerPageMatched}
                  status={'validated'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'suppliers' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadSuppliers
                  fileUploadLineSuppliers={fileUploadGeneralLinesMatched as IFileUploadLineSupplier[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSupplierDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  setOrder={setOrderMatched}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderByMatched}
                  setSelected={setSelectedMatched}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadSupplierLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineSupplier) => void}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setPerPage={setPerPageMatched}
                  status={'validated'}
                />}
              {id === 'vehicles' && fileUploadGeneralLinesMatched && totalRecordsMatched > 0 &&
                <CsvUploadVehicles
                  fileUploadLineVehicle={fileUploadGeneralLinesMatched as IFileUploadLineVehicle[]}
                  deleteHandler={deleteOneUploadLine}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openVehicleDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selectedMatched}
                  setOrder={setOrderMatched}
                  setOrderBy={setOrderByMatched}
                  isSubmitting={isDialogSubmitting}
                  setSelected={setSelectedMatched}
                  validationErrors={validationErrors}
                  editUploadLine={editUploadVehicleLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPageMatched}
                  setInputPage={setInputPageMatched}
                  totalRecords={totalRecordsMatched}
                  perPage={perPageMatched}
                  setPerPage={setPerPageMatched}
                  status={'validated'}
                />}
            </LoadingPage>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <Typography variant='h5' sx={{ color: 'primary.main', mb: 2 }}>Imported Upload Lines</Typography>
            <LoadingPage pageReady={!isLoading}>
              {id === 'emissions' && isStandardEmissions && fileUploadGeneralLines &&
                <CsvUploadEmissions
                  fileUploadLines={fileUploadGeneralLines as IFileUploadLine[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openEmissionDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  uploadId={fileUpload.id}
                  deleteHandler={deleteOneUploadLine}
                  setSelected={setSelected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  editUploadLine={editUploadEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                  billDocuments={isBillUpload ? billDocuments : undefined}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-product' && fileUploadGeneralLines &&
                <CsvUploadGoodEmissions
                  fileUploadLineGoodEmissions={fileUploadGeneralLines as IFileUploadLineGoodEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodEmissionDialog}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  uploadId={fileUpload.id}
                  setSelected={setSelected}
                  editUploadLine={editUploadGoodEmissionLine}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  isSubmitting={isDialogSubmitting}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-direct' && fileUploadGeneralLines &&
                <CsvUploadDirectEmissions
                  fileUploadLineDirectEmissions={fileUploadGeneralLines as IFileUploadLineDirectEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openDirectEmissionDialog}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  isSubmitting={isDialogSubmitting}
                  setOrder={setOrder}
                  uploadId={fileUpload.id}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  editUploadLine={editUploadDirectEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                />}
              {id === 'spend-based' && fileUploadGeneralLines &&
                <CsvUploadSpendBased
                  fileUploadLineSpendBaseds={fileUploadGeneralLines as IFileUploadLineSpendBased[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSpendBasedDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  uploadId={fileUpload.id}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadSpendBasedLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  isSubmitting={isDialogSubmitting}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                />}
              {id === 'locations' && fileUploadGeneralLines &&
                <CsvUploadLocations
                  fileUploadLineLocations={fileUploadGeneralLines as unknown as IFileUploadLineLocation[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openLocationDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setSelected={setSelected}
                  editUploadLine={editUploadLocationLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'vehicles' && fileUploadGeneralLines &&
                <CsvUploadVehicles
                  fileUploadLineVehicle={fileUploadGeneralLines as IFileUploadLineVehicle[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openVehicleDialog}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  validationErrors={validationErrors}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  isSubmitting={isDialogSubmitting}
                  editUploadLine={editUploadVehicleLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                />}
              {id === 'products' && fileUploadGeneralLines &&
                <CsvUploadGood
                  fileUploadLineGoods={fileUploadGeneralLines as IFileUploadLineGood[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  setSelected={setSelected}
                  editUploadLine={editUploadGoodLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'projects' && fileUploadGeneralLines &&
                <CsvUploadProjects
                  fileUploadLineProjects={fileUploadGeneralLines as IFileUploadLineProject[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openProjectDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  deleteHandler={deleteOneUploadLine}
                  setSelected={setSelected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  editUploadLine={editUploadProjectLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineProject) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'suppliers' && fileUploadGeneralLines &&
                <CsvUploadSuppliers
                  fileUploadLineSuppliers={fileUploadGeneralLines as IFileUploadLineSupplier[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSupplierDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadSupplierLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineSupplier) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'completed'}
                />}
            </LoadingPage>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <Typography variant='h5' sx={{ color: 'primary.main', mb: 2 }}>Discarded Upload Lines</Typography>
            <LoadingPage pageReady={!isLoading}>
              {id === 'emissions' && isStandardEmissions && fileUploadGeneralLines &&
                <CsvUploadEmissions
                  fileUploadLines={fileUploadGeneralLines as IFileUploadLine[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openEmissionDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  uploadId={fileUpload.id}
                  deleteHandler={deleteOneUploadLine}
                  setSelected={setSelected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  editUploadLine={editUploadEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  isSubmitting={isDialogSubmitting}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                  billDocuments={isBillUpload ? billDocuments : undefined}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-product' && fileUploadGeneralLines &&
                <CsvUploadGoodEmissions
                  fileUploadLineGoodEmissions={fileUploadGeneralLines as IFileUploadLineGoodEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodEmissionDialog}
                  uploadId={fileUpload.id}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  editUploadLine={editUploadGoodEmissionLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  isSubmitting={isDialogSubmitting}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                />}
              {id === 'emissions' && fileUpload.details === 'emissions-direct' && fileUploadGeneralLines &&
                <CsvUploadDirectEmissions
                  fileUploadLineDirectEmissions={fileUploadGeneralLines as IFileUploadLineDirectEmission[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openDirectEmissionDialog}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setSelected={setSelected}
                  uploadId={fileUpload.id}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  editUploadLine={editUploadDirectEmissionLine}
                  isSubmitting={isDialogSubmitting}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                />}
              {id === 'spend-based' && fileUploadGeneralLines &&
                <CsvUploadSpendBased
                  fileUploadLineSpendBaseds={fileUploadGeneralLines as IFileUploadLineSpendBased[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSpendBasedDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setSelected={setSelected}
                  setOrder={setOrder}
                  uploadId={fileUpload.id}
                  setOrderBy={setOrderBy}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadSpendBasedLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  isSubmitting={isDialogSubmitting}
                  setPerPage={setPerPage}
                  status={'discarded'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'vehicles' && fileUploadGeneralLines &&
                <CsvUploadVehicles
                  fileUploadLineVehicle={fileUploadGeneralLines as IFileUploadLineVehicle[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openVehicleDialog}
                  deleteHandler={deleteOneUploadLine}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  validationErrors={validationErrors}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  isSubmitting={isDialogSubmitting}
                  editUploadLine={editUploadVehicleLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                />}
              {id === 'products' && fileUploadGeneralLines &&
                <CsvUploadGood
                  fileUploadLineGoods={fileUploadGeneralLines as IFileUploadLineGood[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openGoodDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  isSubmitting={isDialogSubmitting}
                  editUploadLine={editUploadGoodLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine}
                  deleteHandler={deleteOneUploadLine}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'projects' && fileUploadGeneralLines &&
                <CsvUploadProjects
                  fileUploadLineProjects={fileUploadGeneralLines as IFileUploadLineProject[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openProjectDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  deleteHandler={deleteOneUploadLine}
                  setSelected={setSelected}
                  setOrder={setOrder}
                  setOrderBy={setOrderBy}
                  isSubmitting={isDialogSubmitting}
                  editUploadLine={editUploadProjectLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineProject) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'suppliers' && fileUploadGeneralLines &&
                <CsvUploadSuppliers
                  fileUploadLineSuppliers={fileUploadGeneralLines as IFileUploadLineSupplier[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openSupplierDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadSupplierLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineSupplier) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
              {id === 'locations' && fileUploadGeneralLines &&
                <CsvUploadLocations
                  fileUploadLineLocations={fileUploadGeneralLines as IFileUploadLineLocation[]}
                  editClickHandler={editFileUploadLineClickHandler}
                  openDialog={openLocationDialog}
                  setValidatedUploadLines={setValidatedUploadLines}
                  selected={selected}
                  setOrder={setOrder}
                  isSubmitting={isDialogSubmitting}
                  setOrderBy={setOrderBy}
                  setSelected={setSelected}
                  deleteHandler={deleteOneUploadLine}
                  editUploadLine={editUploadLocationLine}
                  closeDialogHandler={closeDialogHandler}
                  submitHandler={submitEditUploadLine as unknown as (lineId: string, editUploadLine: IEditFileUploadLineLocation) => void}
                  inputPage={inputPage}
                  setInputPage={setInputPage}
                  totalRecords={totalRecords}
                  perPage={perPage}
                  setPerPage={setPerPage}
                  status={'discarded'}
                  errorCode={errorCode || 0}
                  errorMessage={errorMessage || ''}
                />}
            </LoadingPage>
          </CustomTabPanel>
          {isBillUpload &&
            <CustomTabPanel value={value} index={BILLS_TAB_INDEX}>
              <Typography variant='h5' sx={{ color: 'primary.main', mb: 1 }}>Bills</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                Every bill in this upload and what became of it. Open a bill to see what we read off it and to view the document.
              </Typography>
              {isBillDocumentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
                  <LoadingSpinner />
                </Box>
              ) : (
                <BillDocumentsList
                  documents={billDocuments}
                  fileUploadId={fileUpload.id}
                  onChanged={() => setChange(c => c + 1)}
                  filter={effectiveBillListFilter}
                  onFilterChange={setBillListFilter}
                  readOnly={!!isReportPeriodLocked}
                />
              )}
            </CustomTabPanel>}
          <CustomTabPanel value={value} index={IN_PROGRESS_TAB_INDEX}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <ImportProcessingIndicator
                percent={commitProgress?.toCommitCount ? Math.min(100, Math.round(((Number(commitProgress.numberOfRecords || 0) - Number(commitProgress.alreadyCommittedCount || 0)) / Number(commitProgress.toCommitCount)) * 100)) : 0}
                rowsDone={Math.max(0, Number(commitProgress?.numberOfRecords || 0) - Number(commitProgress?.alreadyCommittedCount || 0))}
                rowsTotal={commitProgress?.toCommitCount || undefined}
                complete={(commitProgress?.inProgressCount ?? 0) === 0}
                indeterminate={!commitProgress?.toCommitCount}
                title="Importing your data"
                subtitle="Bringing your records into the system"
                steps={[
                  { short: 'Importing', long: 'Importing your records' },
                  { short: 'Calculating', long: 'Calculating emissions' },
                  { short: 'Finalising', long: 'Finalising' },
                ]}
                messages={[
                  'Importing your records…',
                  'Calculating emissions…',
                  'Applying emission factors…',
                  'Almost there…',
                ]}
                unitLabel="records"
              />
            </Box>
          </CustomTabPanel>
        </>}
      {openDeleteFileDialog && uploadId && id && fileUpload && (id === 'emissions' || id === 'spend-based') &&
        <DeleteImportFileDialog
          open={openDeleteFileDialog}
          uploadId={uploadId}
          fileType={fileUpload.details}
          isReportPeriodLocked={!!isReportPeriodLocked}
          closeHandler={() => setOpenDeleteFileDialog(false)}
          title={'Delete Import File'}
          cancelHandler={() => setOpenDeleteFileDialog(false)}
          submitHandler={deleteFileHandler}
          isSubmitting={isDialogSubmitting}
        />}
      {openTransactionDialog && fileUpload && (id === 'emissions' || id === 'spend-based') &&
        <EditBulkUploadLinesDialog
          open={openTransactionDialog}
          closeHandler={closeTransactionDialogHandler}
          dialogDetailsIds={selectedPending}
          successHandler={successTransactionDialogHandler}
          fileType={fileUpload.details}
          create={!isDecoupledCommitType}
          isEditAll={selectAllFilteredPending}
          fileUploadId={fileUpload.id}
          showMenuItems={
            id === 'emissions' && fileUpload?.details === 'emissions-direct'
              ? ['EDIT_GROUP', 'EDIT_UNITS_CONSUMED', 'EDIT_CATEGORY', 'EDIT_DATA_TYPE', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_ENERGY_KWH', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME']
              : id === 'emissions' && fileUpload?.details === 'emissions-product'
                ? ['EDIT_PRODUCT', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT']
                : id === 'spend-based'
                  ? ['EDIT_GROUP', 'EDIT_ACTIVITY', 'EDIT_TOTAL', 'EDIT_INVOICE_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME', 'EDIT_BANK_ACCOUNT_NAME']
                  : id === 'emissions' && fileUpload?.details === 'emissions-rules'
                    ? ['EDIT_RULE', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME']
                    : id === 'emissions' && fileUpload?.details === 'emissions-manual'
                      ? ['EDIT_CONVERSION', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME', 'EDIT_SUPPLIER_EMISSION_RATE']
                      : []
          }
          reportPeriodId={fileUpload.reportPeriodId}
        />
      }
    </PageOuterContainer>
  )
}